import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Users, AlertCircle, Calendar, CheckCircle, ListTodo, Clock,
  Star, Activity, UserCheck, BarChart2, UserCog, PhoneCall, Trophy, XCircle,
  Wallet, TrendingDown, TrendingUp, IndianRupee, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BestPerformerCard from "./BestPerformerCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManagerDashboard = ({ managerMetrics, currentUser, businessId }) => {
  const [salaryPreview, setSalaryPreview] = useState(null);
  const [loadingSalary, setLoadingSalary] = useState(true);

  // Filter for "My Salary Preview" — "this" | "last" | "range"
  const today = new Date();
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const [filterMode, setFilterMode] = useState("this");
  const [rangeStart, setRangeStart] = useState(ymd(monthStart(today)));
  const [rangeEnd, setRangeEnd]     = useState(ymd(today));

  const fetchSalaryPreview = async () => {
    setLoadingSalary(true);
    try {
      let qs = "";
      if (filterMode === "this") {
        qs = `?month=${today.getMonth() + 1}&year=${today.getFullYear()}`;
      } else if (filterMode === "last") {
        const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        qs = `?month=${lm.getMonth() + 1}&year=${lm.getFullYear()}`;
      } else if (filterMode === "range" && rangeStart && rangeEnd && rangeStart <= rangeEnd) {
        qs = `?start_date=${rangeStart}&end_date=${rangeEnd}`;
      }
      const res = await fetch(`${BACKEND_URL}/api/attendance/my-salary-preview${qs}`, { credentials: "include" });
      if (res.ok) setSalaryPreview(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingSalary(false); }
  };

  useEffect(() => { fetchSalaryPreview(); }, [filterMode, rangeStart, rangeEnd]); // eslint-disable-line

  // Cross-tab sync — refetch when attendance/leave/salary changes anywhere in this tab.
  useEffect(() => {
    const onChange = (e) => {
      const kind = e?.detail?.kind;
      if (!kind || ["attendance", "leave", "salary"].includes(kind)) fetchSalaryPreview();
    };
    const onFocus = () => fetchSalaryPreview();
    window.addEventListener("hrms:data-changed", onChange);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("hrms:data-changed", onChange);
      window.removeEventListener("focus", onFocus);
    };
  }, []); // eslint-disable-line

  const formatCurrency = (amount) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount || 0);

  if (!managerMetrics) return null;

  const performance = managerMetrics.performance || {};
  const breakdown = performance.breakdown || {};
  const teamSummary = managerMetrics.team_summary || {};
  const employeeBreakdown = managerMetrics.employee_task_breakdown || [];

  return (
    <div className="space-y-5">
      {/* Overview heading — mirrors AdminDashboard for a consistent top-of-page anchor */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            Overview
            {managerMetrics.current_month && (
              <span className="text-xs font-normal text-slate-500 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full">
                {managerMetrics.current_month}
              </span>
            )}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Hi {managerMetrics.user_name || currentUser?.name} — your team snapshot
          </p>
        </div>
      </div>

      {/* Manager Welcome Section */}
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
            <UserCog className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Manager Dashboard</h2>
            <p className="text-violet-100 text-sm">
              Welcome, {managerMetrics.user_name || currentUser?.name}! 
              Here's your team overview for {managerMetrics.current_month}
            </p>
          </div>
        </div>
      </div>

      {/* Personal Productivity Section */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500" />
          My Productivity
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* My To-Do Tasks */}
          <Card
            data-testid="manager-todo-card"
            className="bg-gradient-to-br from-slate-500 to-slate-600 text-white rounded-2xl shadow-lg shadow-slate-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">My To-Do Tasks</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {managerMetrics.my_todo_tasks || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">Assigned & pending</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <ListTodo className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* In Progress */}
          <Card
            data-testid="manager-inprogress-card"
            className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-2xl shadow-lg shadow-blue-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">In Progress</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {managerMetrics.my_in_progress || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">Currently working on</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Today's Completed */}
          <Card
            data-testid="manager-today-completed-card"
            className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl shadow-lg shadow-emerald-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">Today's Completed</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {managerMetrics.today_completed || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">Tasks done today</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Overdue */}
          <Card
            data-testid="manager-overdue-card"
            className="bg-gradient-to-br from-red-500 to-rose-600 text-white rounded-2xl shadow-lg shadow-red-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">Overdue Tasks</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {managerMetrics.total_overdue || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">Past due date</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Completed */}
          <Card
            data-testid="manager-monthly-completed-card"
            className="bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-2xl shadow-lg shadow-violet-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">Monthly Completed</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {managerMetrics.monthly_completed || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">Tasks this month</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance Score Card */}
          <Card
            data-testid="manager-performance-card"
            className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl shadow-lg shadow-amber-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">Performance Score</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {performance.score || 0}
                    <span className="text-lg">/100</span>
                  </h3>
                  <p className="text-xs text-white/60 mt-1">{performance.level || "N/A"}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Lead Performance Section */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-500" />
          Lead Performance
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Leads */}
          <Card
            data-testid="manager-total-leads-card"
            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">Total Leads</p>
                  <h3 className="text-3xl font-heading font-bold text-slate-900 dark:text-white mt-2">
                    {managerMetrics.total_leads || 0}
                  </h3>
                </div>
                <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Follow-Up Pending */}
          <Card
            data-testid="manager-followup-pending-card"
            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">Follow-Up Pending</p>
                  <h3 className="text-3xl font-heading font-bold text-amber-600 dark:text-amber-400 mt-2">
                    {managerMetrics.followup_pending || 0}
                  </h3>
                </div>
                <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                  <PhoneCall className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Converted Leads */}
          <Card
            data-testid="manager-converted-leads-card"
            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">Converted Leads</p>
                  <h3 className="text-3xl font-heading font-bold text-emerald-600 dark:text-emerald-400 mt-2">
                    {managerMetrics.converted_leads || 0}
                  </h3>
                </div>
                <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lost Leads */}
          <Card
            data-testid="manager-lost-leads-card"
            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">Lost Leads</p>
                  <h3 className="text-3xl font-heading font-bold text-red-600 dark:text-red-400 mt-2">
                    {managerMetrics.lost_leads || 0}
                  </h3>
                </div>
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Team Summary Cards */}
      {teamSummary.team_size > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-violet-500" />
            Team Summary
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl shadow-lg">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{teamSummary.total_completed}</p>
                <p className="text-xs text-white/80">Tasks Completed</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-500 to-slate-600 text-white rounded-2xl shadow-lg">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{teamSummary.total_pending}</p>
                <p className="text-xs text-white/80">Tasks Pending</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-2xl shadow-lg">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{teamSummary.total_in_progress}</p>
                <p className="text-xs text-white/80">In Progress</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-2xl shadow-lg">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{teamSummary.team_size}</p>
                <p className="text-xs text-white/80">Team Members</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Employee-Wise Task Breakdown Table */}
      {employeeBreakdown.length > 0 && (
        <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <UserCheck className="w-5 h-5 text-indigo-600" />
              Employee-Wise Task Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Employee</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-emerald-600">Completed</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Pending</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-blue-600">In Progress</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Total</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeBreakdown.map((employee, index) => {
                    const bgColors = ['bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-violet-500'];
                    const bgColor = bgColors[index % bgColors.length];
                    const initial = employee.name ? employee.name.charAt(0).toUpperCase() : '?';
                    
                    return (
                      <tr key={employee.user_id} className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ${bgColor}`}>
                              {initial}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">{employee.name}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{employee.role}</p>
                            </div>
                          </div>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-bold">
                            {employee.tasks_completed}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold">
                            {employee.tasks_pending}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-bold">
                            {employee.tasks_in_progress}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="font-bold text-slate-900 dark:text-white">{employee.total_tasks}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-500"
                                style={{ width: `${employee.completion_percentage}%` }}
                              />
                            </div>
                            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 w-12 text-right">
                              {employee.completion_percentage}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============ BEST PERFORMER OF THE MONTH ============ */}
      <BestPerformerCard businessId={businessId} />

      {/* Quick Actions */}
      <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <a href="/work-hub/tasks" className="flex flex-col items-center gap-2 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors cursor-pointer">
              <ListTodo className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">View Tasks</span>
            </a>
            <a href="/leads" className="flex flex-col items-center gap-2 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors cursor-pointer">
              <Users className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Manage Leads</span>
            </a>
            <a href="/calendar" className="flex flex-col items-center gap-2 p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors cursor-pointer">
              <Calendar className="w-6 h-6 text-violet-600 dark:text-violet-400" />
              <span className="text-sm font-medium text-violet-700 dark:text-violet-300">Calendar</span>
            </a>
            <a href="/settings" className="flex flex-col items-center gap-2 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors cursor-pointer">
              <UserCog className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Settings</span>
            </a>
          </div>
        </CardContent>
      </Card>
      {/* Salary Preview */}
      <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold flex flex-wrap items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-600" />
            <span>
              My Salary Preview
              {" — "}
              {salaryPreview?.is_range
                ? `${salaryPreview.date_range_start} → ${salaryPreview.date_range_end}`
                : salaryPreview
                  ? `${new Date(0, salaryPreview.month - 1).toLocaleString('default', { month: 'long' })} ${salaryPreview.year}`
                  : ""}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchSalaryPreview}
              disabled={loadingSalary}
              className="ml-auto h-7 w-7 p-0"
              title="Refresh"
              data-testid="refresh-my-salary"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingSalary ? "animate-spin" : ""}`} />
            </Button>
          </CardTitle>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            {[
              { key: "this", label: "This Month" },
              { key: "last", label: "Last Month" },
              { key: "range", label: "Custom Range" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setFilterMode(opt.key)}
                className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                  filterMode === opt.key
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600"
                }`}
                data-testid={`salary-filter-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
            {filterMode === "range" && (
              <div className="flex items-center gap-2 ml-1">
                <input
                  type="date"
                  value={rangeStart}
                  max={rangeEnd}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="h-8 px-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs"
                  data-testid="salary-range-start"
                />
                <span className="text-slate-400">→</span>
                <input
                  type="date"
                  value={rangeEnd}
                  min={rangeStart}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="h-8 px-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs"
                  data-testid="salary-range-end"
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingSalary ? (
            <p className="text-center text-slate-400 py-4">Loading salary...</p>
          ) : salaryPreview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 text-center">
                  <p className="text-xs text-slate-500">Working Days</p>
                  <p className="text-xl font-bold text-slate-800 dark:text-white">{salaryPreview.working_days}</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center">
                  <p className="text-xs text-emerald-600">Present</p>
                  <p className="text-xl font-bold text-emerald-600">{salaryPreview.present_days}</p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center">
                  <p className="text-xs text-amber-600">Half Days</p>
                  <p className="text-xl font-bold text-amber-600">{salaryPreview.half_days}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-center">
                  <p className="text-xs text-red-600">Absent</p>
                  <p className="text-xl font-bold text-red-600">{salaryPreview.absent_days}</p>
                </div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Basic Salary</span>
                  <span className="font-medium">{formatCurrency(salaryPreview.basic_salary)}</span>
                </div>
                {salaryPreview.hra > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">HRA</span><span className="font-medium">{formatCurrency(salaryPreview.hra)}</span></div>}
                {salaryPreview.conveyance > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">Conveyance</span><span className="font-medium">{formatCurrency(salaryPreview.conveyance)}</span></div>}
                {salaryPreview.other_allowances > 0 && <div className="flex justify-between text-sm"><span className="text-slate-600">Other Allowances</span><span className="font-medium">{formatCurrency(salaryPreview.other_allowances)}</span></div>}
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-slate-600 font-medium">Gross Salary</span>
                  <span className="font-bold">{formatCurrency(salaryPreview.gross_salary)}</span>
                </div>
                {salaryPreview.total_deductions > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Total Deductions</span>
                    <span className="font-medium">-{formatCurrency(salaryPreview.total_deductions)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base border-t pt-2 mt-1">
                  <span className="font-bold text-slate-800 dark:text-white">Estimated Net Salary</span>
                  <span className="font-bold text-emerald-600 text-lg">{formatCurrency(salaryPreview.net_payable)}</span>
                </div>
              </div>
              {salaryPreview.total_deductions > 0 && (
                <div className="bg-red-50 dark:bg-red-900/10 rounded-xl p-3 space-y-1">
                  <p className="text-xs font-semibold text-red-700 mb-1">Deduction Breakdown</p>
                  {salaryPreview.absent_deduction > 0 && <div className="flex justify-between text-xs"><span className="text-red-600">Absent ({salaryPreview.absent_days} days)</span><span className="text-red-600">-{formatCurrency(salaryPreview.absent_deduction)}</span></div>}
                  {salaryPreview.half_day_deduction > 0 && <div className="flex justify-between text-xs"><span className="text-red-600">Half Days ({salaryPreview.half_days})</span><span className="text-red-600">-{formatCurrency(salaryPreview.half_day_deduction)}</span></div>}
                  {salaryPreview.late_deduction > 0 && <div className="flex justify-between text-xs"><span className="text-red-600">Late ({salaryPreview.late_count})</span><span className="text-red-600">-{formatCurrency(salaryPreview.late_deduction)}</span></div>}
                  {salaryPreview.pf_deduction > 0 && <div className="flex justify-between text-xs"><span className="text-red-600">PF</span><span className="text-red-600">-{formatCurrency(salaryPreview.pf_deduction)}</span></div>}
                  {salaryPreview.esi_deduction > 0 && <div className="flex justify-between text-xs"><span className="text-red-600">ESI</span><span className="text-red-600">-{formatCurrency(salaryPreview.esi_deduction)}</span></div>}
                  {salaryPreview.tds_deduction > 0 && <div className="flex justify-between text-xs"><span className="text-red-600">TDS</span><span className="text-red-600">-{formatCurrency(salaryPreview.tds_deduction)}</span></div>}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-slate-400">
              <p>Salary information not available</p>
              <p className="text-xs mt-1">Contact HR to set up your salary structure</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManagerDashboard;
